import LineByLineTextErrorsComponent from "./LineByLineTextErrorsComponent";
import {useEffect, useRef} from "react";
import {TmParsingError} from "../../js/errors/Exceptions";
import TmStateComponent from "./TmStateComponent";
import {containsEqualElement, convertStrToInt, trimStringByChar, exportTextFile} from "../../js/utils.js";
import TmTransitionRuleComponent from "./TmTransitionRuleComponent";
import TapeComponent from "./TapeComponent";
import {TuringMachine} from "../../js/entities/TuringMachine/TuringMachine";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCaretLeft} from "@fortawesome/free-solid-svg-icons";
import {faCaretRight} from "@fortawesome/free-solid-svg-icons/faCaretRight";


export default function TmRunModeComponent({runModeState,
                                               setRunModeState,
                                               switchToCreateMode}) {
    const MIN_STEPS_LIMIT = 1;
    const MAX_STEPS_LIMIT = 1_000;

    let tmRef = useRef();
    let tm = tmRef.current; // TmTuringMachine. undefined, when TM is not created

    // create Turing Machine
    useEffect(() => {
        if(runModeState.initialWordOnTape !== ''){ // prevent from creating TM with empty input string
           tmRef.current = new TuringMachine(
               runModeState.states,
               runModeState.alphabet,
               runModeState.transitionsRelation,
               runModeState.initialState,
               runModeState.finalStates,
               runModeState.initialWordOnTape
           );

           setRunModeState((state) => ({
               ...state,
               history: [tmRef.current.saveState()],
               currentIndexInHistory: 0
           }));

           console.log(`TM is (re-)created, initial word is: ${runModeState.initialWordOnTape}`);
        }
    }, [runModeState.initialWordOnTape]);


    // focus on highlighted state and highlighted transition rule
    useEffect(() => {
        let highlightedStates=
            document.getElementsByClassName('state-highlighted');
        if(highlightedStates.length > 0){
            highlightedStates[0].scrollIntoView({ behavior: "smooth", block: "center" });
        }

        let highlightedTransitionRules=
            document.getElementsByClassName('transition-rule-highlighted');
        if(highlightedTransitionRules.length > 0){
            highlightedTransitionRules[0].scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [runModeState]);

    const onWordInputChanged = (event) => {
        event.preventDefault();

        let newWordInput = event.target.value;   // string
        setRunModeState((state) => ({
            ...state,
            wordInput: newWordInput,
            wordInputErrors: validateWordInput(newWordInput),
        }));
    }

    const onPutInputWordOnTape = (event) => {
        event.preventDefault();

        if(runModeState.wordInputErrors.length > 0) { // impossible case, beacuse button is disabled
            return;
        }

        let errors = validateWordInput(runModeState.wordInput);
        let newInitialWordOnTape;
        if(errors.length === 0) {
            newInitialWordOnTape = trimStringByChar(runModeState.wordInput, runModeState.alphabet.emptyCellChar);
            if(newInitialWordOnTape === '') {
                newInitialWordOnTape = runModeState.alphabet.emptyCellChar;
            }
        }

        setRunModeState((state) => ({
            ...state,
            wordInputErrors: errors,
            initialWordOnTape: (errors.length === 0)
                ? newInitialWordOnTape
                : state.initialWordOnTape // don't change
        }));
    }

    /**
     * Validates given <code>wordInput</code> and gives list of errors for it.
     * @param wordInput {string}
     * @return {TmParsingError[]}
     */
    const validateWordInput = (wordInput) => {
        let errors = [];
        if(wordInput.length === 0) {
            errors.push(new TmParsingError('word-input', undefined, 'Input word can\'t be empty'));
        }
        else{
            if(/\s/.test(wordInput)){ // contains whitespace
                errors.push(new TmParsingError('word-input', undefined,
                    'Input word can\'t contain whitespaces'));
            }

            // check if wordInput contains non-alphabet char
            for(const char of [...new Set(wordInput)]){ // iterate over unique chars of wordInput
                if(!/\s/.test(char) && // don't consider whitespaces, because we have already added an error about them
                    !runModeState.alphabet.includes(char)){
                    errors.push(new TmParsingError('word-input', undefined,
                        `Character '${char}' is not part of the alphabet`));
                }
            }
        }
        return errors;
    }


    const isTuringMachineCreated = () => {
        return runModeState.history.length > 0;
    }

    /**
     * Returns current state that must be displayed.
     *
     * The only method that should be used to get current state (instead of calling history and currentIndexInHistory).
     *
     * Returns <code>undefined</code> only if TM is not initialized (no word on the tape)
     *
     * @returns {TmMemento|undefined}
     */
    const getCurrentTmState = () => {
        if(!isTuringMachineCreated()){
            return undefined;
        }
        return runModeState.history[runModeState.currentIndexInHistory];
    }

    const getCurrentWordOnTape = () => {
        let tmState = getCurrentTmState();
        if(!tmState){
            return tmState;
        }

        return tmState.tape.join('');
    }

    /**
     * @returns {TmTransitionRule[]|undefined}
     */
    const getAvailableTransitionRules = () => {
        if(!isTuringMachineCreated()){
            return undefined;
        }

        let currentTmMemento = getCurrentTmState();
        let possibleTransitionRules = runModeState.transitionsRelation
            .getPossibleTransitionRules(
                currentTmMemento.currentState,
                currentTmMemento.tape[currentTmMemento.currentHeadPosition]
            );
        return possibleTransitionRules ? possibleTransitionRules : [];
    }

    /**
     * Checks if TM is finished, according to <code>getCurrentTmState()</code>
     * @returns {boolean|undefined}
     */
    const isTmFinished = () => {
        let tmState = getCurrentTmState();
        if(!tmState){
            return tmState;
        }

        return containsEqualElement(runModeState.finalStates, tmState.currentState);
    }


    /**
     * @param newMemento {TmMemento}
     */
    const addNewMementoAndIncrementCurrentIndexInHistory = (newMemento) => {
        setRunModeState((state) => ({
            ...state,
            history: [...state.history, newMemento],
            currentIndexInHistory: state.currentIndexInHistory + 1
        }));
    }

    const onBackToTheInitState = (event) => {
        event.preventDefault();

        setRunModeState((state) => ({
            ...state,
            currentIndexInHistory: 0
        }));
    }

    const onStepBackward = (event) => {
        event.preventDefault();

        if(runModeState.currentIndexInHistory === 0){ // impossible case (button is disabled)
            return;
        }

        setRunModeState((state) => ({ // decrement
            ...state,
            currentIndexInHistory: state.currentIndexInHistory - 1
        }));
    }

    const onStepForward = (event) => {
        event.preventDefault();

        if(isTmFinished() || getAvailableTransitionRules().length === 0){ // impossible cases (button is disabled)
            return;
        }

        if(runModeState.currentIndexInHistory < runModeState.history.length - 1){ // history contains result of step forward
            setRunModeState((state) => ({ // increment
                ...state,
                currentIndexInHistory: state.currentIndexInHistory + 1
            }));
        }
        else{ // otherwise, really need to take a step
            tm.restoreState(getCurrentTmState());
            tm.executeNextStep();
            addNewMementoAndIncrementCurrentIndexInHistory(tm.saveState());
        }
    }

    const onRunUntilFinishAndNotExceedMaxSteps = (event) => {
        event.preventDefault();

        if(isTmFinished() || getAvailableTransitionRules().length === 0){ // impossible cases (button is disabled)
            return;
        }

        // check, if this calculation have already been done
        let lastTmMemento = runModeState.history.at(-1);
        if(runModeState.currentIndexInHistory + runModeState.stepsLimit < runModeState.history.length){
            console.log('calc skipped: 1');
            setRunModeState((state) => ({
                ...state,
                currentIndexInHistory: state.currentIndexInHistory + state.stepsLimit
            }));
            return;
        }
        else if(containsEqualElement(runModeState.finalStates, lastTmMemento.currentState) ||
            !runModeState.transitionsRelation.getPossibleTransitionRules(
                lastTmMemento.currentState,
                lastTmMemento.tape[lastTmMemento.currentHeadPosition])){ // finished or stuck
            console.log('calc skipped: 2');
            setRunModeState((state) => ({
                ...state,
                currentIndexInHistory: state.history.length - 1
            }));
            return;
        }
        // otherwise, really need to make calculations

        // actually calculations
        tm.restoreState(getCurrentTmState());
        let steps = 0;
        let furtherHistory = [];
        while(!tm.isFinished() && !tm.isStuck() && steps < runModeState.stepsLimit){ // main exec loop
            tm.executeNextStep();
            furtherHistory.push(tm.saveState());
            steps++;
        }

        let historyUpdated =
            runModeState.history.slice(0, runModeState.currentIndexInHistory + 1) // history up to currentIndexInHistory (inclusively)
                .concat(furtherHistory); // concat history generated by steps

        // update view
        setRunModeState((state) => ({
            ...state,
            history: historyUpdated,
            currentIndexInHistory: historyUpdated.length - 1
        }));
    }


    const onSwitchToCreateMode = (event) => {
        event.preventDefault();

        switchToCreateMode();
    }

    const onExportCalculations = (event) => {
        event.preventDefault();

        if(runModeState.history.length === 0){ // impossible case (button is disabled)
            return;
        } // here, TM is created

        let result = '';

        // print Turing machine
        result +=
            "========== TURING MACHINE ==========\n" +
            tm.toString() +
            "\n";

        // print all TmMemento from history
        result += "==========      STEPS     ==========\n\n";
        runModeState.history.forEach((memento, index) => {
            if (index === 0){
                result += `0) (initial, before calculations)\n`;
            }
            else{
                result += `${index})\n`;
            }

            result += `State: ${memento.currentState.name}\n`;
            result +=
                'Tape: ' +
                memento.tape.join('') + '\n' +
                '      ' + ' '.repeat(memento.currentHeadPosition) + '^';
            result += "\n\n";
        });

        // add info about finishing; check info about last memento
        tm.restoreState(runModeState.history.at(-1));
        if(tm.isFinished()){
            result += "FINISHED";
        }
        else{
            if(tm.isStuck()){
                result += "NOT FINISHED (STUCK)";
            }
            else{
                result += "NOT FINISHED";
            }
        }

        exportTextFile("tm_calculations.txt", result);
    }

    let availableTransitionRules = getAvailableTransitionRules(); // use it for rendering
    // undefined, if TM is not created

    return(
        <>
            {/* Input for input word */}
            <div className="row">
                <div className="row col-9">
                    <label className={'col-2 text-end'} htmlFor={'input-word'}>Enter word</label>

                    <div className="col">
                        {/* input */}
                        <input name="word-input" id='word-input' type='text' required
                               className={"form-control col"} value={runModeState.wordInput}
                               onChange={onWordInputChanged} />

                        {/* parsing errors for input */}
                        <LineByLineTextErrorsComponent errors={runModeState.wordInputErrors} />
                    </div>
                </div>

                {/* button to put input word on the tape */}
                <div className="col-2">
                    <button type="button" className="btn btn-primary btn m-0 mb-0"
                            disabled={runModeState.wordInputErrors.length > 0}
                            onClick={onPutInputWordOnTape}>
                        <p className={'m-0 p-0'}>{'Put on the tape'}</p>
                    </button>
                </div>
            </div>

            {/* Tape */}
            <div className="row m-0">
                {(isTuringMachineCreated()) ? (
                    <TapeComponent
                        tape={getCurrentTmState().tape}
                        highlightedCellIndex={getCurrentTmState().currentHeadPosition}
                        emptyCellChar={runModeState.alphabet.emptyCellChar}
                        needToHighlight={true}
                    />
                ) : (
                    <TapeComponent
                        tape={[runModeState.alphabet.emptyCellChar]}
                        highlightedCellIndex={0}
                        emptyCellChar={runModeState.alphabet.emptyCellChar}
                        needToHighlight={false}
                    />
                )}
            </div>

            {/* Initial and current words on the tape */}
            <div className="row m-0 text-muted small">
                {(isTuringMachineCreated()) ? (
                    <>
                        <div className="col-2 p-1 single-line-ellipsis-text-container text-end">
                            <b>Initial word on the tape:</b>
                        </div>
                        <div className="col-4 p-1 single-line-ellipsis-text-container text-start">
                            {runModeState.initialWordOnTape}
                        </div>

                        <div className="col-2 p-1 single-line-ellipsis-text-container text-end">
                            <b>Current word on the tape:</b>
                        </div>
                        <div className="col-4 p-1 single-line-ellipsis-text-container text-start">
                            {getCurrentWordOnTape()}
                        </div>
                    </>
                ) : (
                    <div className={'p-1 single-line-ellipsis-text-container'}>
                        No word was put on the tape
                    </div>
                )}
            </div>


            {/* Display Alphabet */}
            <div className="row mt-3">
            <div className="col-2 p-1">
                    <b>Alphabet</b>
                </div>
                <div className="col-7 p-1 text-start alphabet-chars-container">
                    {runModeState.alphabet.chars.join(' ')}
                </div>

                <div className="col-2 p-1 text-end">
                    <b>Empty cell char</b>
                </div>
                <div className="col-1 p-1 text-start">
                    <div className="alphabet-chars-container">
                        {runModeState.alphabet.emptyCellChar}
                    </div>
                </div>
            </div>

            {/* Display states and transitions relation */}
            <div className="row mt-3">

                {/* States */}
                <div className="col-6">
                    <h5 className="text-center">States</h5>
                    {/* header: name, description, isFinal */}
                    <div className="row m-0 mb-2">
                        <div className="col-3 p-0">
                            <p className="m-0 p-0">Name</p>
                        </div>

                        <div className="col-5 p-0">
                            <p className="m-0 p-0">Description</p>
                        </div>

                        <div className="col-4 p-0">
                            {/* Empty place finished -> */}
                        </div>
                    </div>
                    <div className="height-limited-scroll-container-middle">
                        {
                            runModeState.states
                                .map((s, idx) => (
                                    <TmStateComponent
                                        state={s}
                                        isInitial={runModeState.initialState.equals(s)}
                                        isFinal={containsEqualElement(runModeState.finalStates, s)}
                                        isHighlighted={isTuringMachineCreated() &&
                                            getCurrentTmState().currentState.equals(s)}
                                        key={idx}
                                    />
                                ))
                        }
                    </div>
                </div>

                {/* Transitions relation */}
                <div className="col-6">
                    <h5 className="text-center">Transitions relation</h5>
                    {/* header: start state, looked char, ->, final state, writeChar, L/R/[empty] */}
                    <div className="row m-0 mb-2">
                        <div className="col-2 p-0">
                            <p className="m-0 p-0">Start state</p>
                        </div>

                        <div className="col-2 p-0">
                            <p className="m-0 p-0">Looked char</p>
                        </div>

                        <div className="col-2 p-0">
                            {/* Empty place for -> */}
                        </div>

                        <div className="col-2 p-0">
                            <p className="m-0 p-0">End state</p>
                        </div>

                        <div className="col-2 p-0">
                            <p className="m-0 p-0">Char to write</p>
                        </div>

                        <div className="col-2 p-0">
                            <p className="m-0 p-0">Head dir</p>
                        </div>
                    </div>

                    <div className="height-limited-scroll-container-middle">
                        {
                            runModeState.transitionsRelation.plainTransitionRules
                                .map((r, idx) => (
                                    <TmTransitionRuleComponent
                                        transitionRule={r}
                                        isHighlighted={isTuringMachineCreated() && !isTmFinished() &&
                                            containsEqualElement(availableTransitionRules, r)}
                                        key={idx}
                                    />
                                ))
                        }
                    </div>

                    {/* Display how many transitions are available */}
                    <div className="text-muted small d-flex flex-row justify-content-end">
                        {(isTuringMachineCreated() && !isTmFinished() && availableTransitionRules.length === 0) && (
                            <>
                                No transition available (stuck)
                            </>
                        )}
                        {(isTuringMachineCreated() && !isTmFinished() && availableTransitionRules.length === 1) && (
                            <>
                                One transition available
                            </>
                        )}
                        {(isTuringMachineCreated() && !isTmFinished() && availableTransitionRules.length > 1) && (
                            <>
                                Undetermined choice: {availableTransitionRules.length} transitions are available
                                (one will be chosen randomly)
                            </>
                        )}
                    </div>

                </div>
            </div>

            {/* Controls */}
            <div className="row m-0 mt-1">

                {/* Is finished, Executed steps count */}
                <div className="col-2 m-0">
                    {isTuringMachineCreated() && (
                        <>
                            {(isTmFinished()) ? (
                                <p className="m-0 text-true"><b>FINISHED</b></p>
                            ) : (
                                (availableTransitionRules.length > 0) ? (
                                    <p className="m-0 text-false"><b>NOT FINISHED</b></p>
                                ) : (
                                    <p className="m-0 text-false"><b>NOT FINISHED</b> (STUCK)</p>
                                )

                            )}

                            <p className="m-0">
                                <b>EXECUTED STEPS: </b>
                                {getCurrentTmState().executedStepsCount}
                            </p>
                        </>
                    )}
                </div>

                {/* Space */}
                <div className="col-1">

                </div>

                {/* Run until finish button */}
                <div className="col-2 text-end">
                    <button type="button" className="btn btn-primary m-1"
                            disabled={!isTuringMachineCreated() || isTmFinished() || availableTransitionRules.length === 0}
                            onClick={onRunUntilFinishAndNotExceedMaxSteps}>
                        RUN UNTIL FINISH
                    </button>
                </div>

                {/* Steps limit input */}
                <div className="col-1 text-start">
                    <label htmlFor="steps-limit-input" className='small'>Steps limit:</label>
                    <input id='steps-limit-input' type='number' required
                           min={MIN_STEPS_LIMIT} max={MAX_STEPS_LIMIT} step='1'
                           value={runModeState.stepsLimit}
                           className='form-control p-0'
                           disabled={!isTuringMachineCreated()}

                           onChange={(e) => {
                               e.preventDefault();
                               setRunModeState((state) => ({
                                   ...state,
                                   stepsLimit: Math.min(
                                       Math.max(convertStrToInt(e.target.value, MIN_STEPS_LIMIT), MIN_STEPS_LIMIT),
                                       MAX_STEPS_LIMIT)
                               }));
                           }}
                    />
                </div>

                <div className="col-6">
                    <div className="d-flex justify-content-center">
                        {/* button for back to the initial state of TM */}
                        <button type="button" className="btn btn-primary btn-lg m-1 mb-0"
                                disabled={!isTuringMachineCreated() || runModeState.currentIndexInHistory === 0}
                                onClick={onBackToTheInitState}>
                            <p className={'m-0 p-0'}>{'TO START'}</p>
                        </button>

                        {/* one step backward */}
                        <button type="button" className="btn btn-primary btn-lg m-1 mb-0"
                                disabled={!isTuringMachineCreated() || runModeState.currentIndexInHistory === 0}
                                onClick={onStepBackward}>
                            <p className={'m-0 p-0'}><FontAwesomeIcon icon={faCaretLeft}/> step</p>
                        </button>

                        {/* one step forward */}
                        <button type="button" className="btn btn-primary btn-lg m-1 mb-0"
                                disabled={!isTuringMachineCreated() || isTmFinished() || availableTransitionRules.length === 0}
                                onClick={onStepForward}>
                            <p className={'m-0 p-0'}>step <FontAwesomeIcon icon={faCaretRight}/></p>
                        </button>
                    </div>
                </div>

            </div>


            <div className="d-flex flex-row justify-content-around mt-2 mb-5">
                {/* Export calculations */}
                <button type="button" className="btn btn-primary btn-lg m-2 mb-0"
                        disabled={runModeState.history.length === 0}
                        onClick={onExportCalculations}>
                    <p className={'m-0 p-0'}>{'Export calculations'}</p>
                </button>

                {/* button to go to CREATE mode */}
                <button type="button" className="btn btn-primary btn-lg m-2 mb-0"
                        disabled={false}
                        onClick={onSwitchToCreateMode}>
                    <p className={'m-0 p-0'}>{'Back to CREATE mode'}</p>
                </button>
            </div>
        </>
    )
}